import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { captureError } from '../services/errorTracking';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback custom (remplace le fallback par défaut) */
  fallback?: ReactNode;
  /** Niveau : 'page' affiche un écran plein, 'section' affiche un bloc inline */
  level?: 'page' | 'section';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** True si l'erreur correspond à un chunk JS/CSS introuvable (typiquement
   *  HTML cached qui pointe vers un hash de chunk supprimé après deploy). */
  isChunkLoadError: boolean;
}

const CHUNK_RELOAD_KEY = 'fitd-chunk-reload-attempt';
const CHUNK_RELOAD_TTL_MS = 60_000; // pas plus d'un reload auto par minute

/**
 * Détecte les erreurs typiques d'un chunk lazy-loaded qui n'existe plus
 * après un nouveau déploiement. Le HTML cached côté CDN/browser pointe
 * vers un ancien hash, le fichier renvoie 404, l'import dynamique throw.
 */
function isChunkLoadError(error: Error): boolean {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message || '';
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // Safari iOS: balance "'text/html' is not a valid JavaScript MIME type."
    // au lieu du message Chrome verbose. Ce pattern couvre toutes les variantes.
    /is not a valid JavaScript MIME type/i.test(msg)
  );
}

/**
 * Error Boundary React — capture les erreurs JS dans l'arbre enfant
 * et affiche un fallback au lieu d'un écran blanc.
 *
 * Usage :
 *   <ErrorBoundary>
 *     <MonComposant />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary level="section">
 *     <Widget />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkLoadError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, isChunkLoadError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Si c'est un chunk error (HTML cached pointant vers un hash de chunk
    // disparu après deploy), on tente un reload automatique avant tout —
    // l'utilisateur voit juste un flash plutôt qu'une page d'erreur.
    // Flag de session pour éviter une boucle de reload si le chunk est
    // vraiment manquant côté CDN (cas extrême).
    if (isChunkLoadError(error)) {
      try {
        const lastAttemptRaw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        const lastAttempt = lastAttemptRaw ? parseInt(lastAttemptRaw, 10) : 0;
        const now = Date.now();
        if (!lastAttempt || now - lastAttempt > CHUNK_RELOAD_TTL_MS) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
          // location.reload() force le navigateur à re-fetch le HTML →
          // récupère le nouvel index.html avec les bons hashs de chunks.
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage indispo (mode privé Safari, etc.) — fallback UI */ }
    }

    captureError(error, {
      componentStack: errorInfo.componentStack,
      level: this.props.level || 'page',
      isChunkLoadError: this.state.isChunkLoadError,
    });

    // Log crash to Firestore for admin visibility. Lazy-import firebase so
    // ErrorBoundary (eager-loaded) doesn't pull the firestore SDK into the
    // critical chunk.
    void (async () => {
      try {
        const [{ collection, addDoc, serverTimestamp }, { db, auth }] = await Promise.all([
          import('firebase/firestore'),
          import('../firebase'),
        ]);
        const user = auth.currentUser;
        await addDoc(collection(db, 'errorLogs'), {
          message: error.message,
          stack: error.stack?.substring(0, 2000) || '',
          componentStack: (errorInfo.componentStack || '').substring(0, 1000),
          level: this.props.level || 'page',
          isChunkLoadError: this.state.isChunkLoadError,
          url: window.location.href,
          userAgent: navigator.userAgent,
          userId: user?.uid || null,
          userEmail: user?.email || null,
          createdAt: serverTimestamp(),
        });
      } catch { /* best effort, never block rendering */ }
    })();
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null, isChunkLoadError: false });
  };

  private handleGoHome = (): void => {
    window.location.href = '/landing';
  };

  private handleHardReload = (): void => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level || 'page';

    // Chunk error qui a déjà tenté un auto-reload sans succès → fallback
    // dédié qui invite à recharger manuellement plutôt que d'afficher
    // l'écran générique d'erreur (qui serait trompeur — ce n'est pas un
    // bug app, juste un asset périmé).
    if (this.state.isChunkLoadError && level === 'page') {
      return <ChunkUpdateFallback onReload={this.handleHardReload} />;
    }

    if (level === 'section') {
      return <SectionFallback onRetry={this.handleRetry} />;
    }

    return <PageFallback onRetry={this.handleRetry} onGoHome={this.handleGoHome} />;
  }
}

export default ErrorBoundary;

/* ───────── Fallback plein écran ───────── */

function PageFallback({
  onRetry,
  onGoHome,
}: {
  onRetry: () => void;
  onGoHome: () => void;
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#FFFBF5',
        px: 2,
      }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <Typography
          sx={{ fontSize: '4rem', mb: 1, lineHeight: 1 }}
          aria-hidden
        >
          :(
        </Typography>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: '#1C1917', mb: 1.5 }}
        >
          Quelque chose s'est mal passé
        </Typography>

        <Typography
          sx={{ color: '#78716C', mb: 4, maxWidth: 400, mx: 'auto' }}
        >
          Une erreur inattendue est survenue. Vous pouvez réessayer ou revenir à
          l'accueil.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{
              bgcolor: '#166534',
              color: '#fff',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': { bgcolor: '#16A34A' },
            }}
          >
            Réessayer
          </Button>

          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={onGoHome}
            sx={{
              borderColor: '#E7E5E4',
              color: '#1C1917',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': { borderColor: '#166534' },
            }}
          >
            Accueil
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

/* ───────── Fallback chunk update (déploiement détecté) ───────── */

function ChunkUpdateFallback({ onReload }: { onReload: () => void }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#FFFBF5',
        px: 2,
      }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <Typography
          sx={{ fontSize: '3rem', mb: 1, lineHeight: 1 }}
          aria-hidden
        >
          ↻
        </Typography>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: '#1C1917', mb: 1.5 }}
        >
          Une nouvelle version est disponible
        </Typography>

        <Typography
          sx={{ color: '#78716C', mb: 4, maxWidth: 420, mx: 'auto' }}
        >
          On a déployé une mise à jour pendant que tu naviguais. Recharge la
          page pour récupérer la dernière version — tes données ne sont pas
          affectées.
        </Typography>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={onReload}
          sx={{
            bgcolor: '#166534',
            color: '#fff',
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': { bgcolor: '#16A34A' },
          }}
        >
          Recharger
        </Button>
      </Container>
    </Box>
  );
}

/* ───────── Fallback section (inline) ───────── */

function SectionFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 3,
        border: '1px solid #E7E5E4',
        bgcolor: '#FFFFFF',
        my: 2,
      }}
    >
      <Typography sx={{ fontWeight: 600, color: '#1C1917', mb: 1 }}>
        Impossible de charger cette section
      </Typography>

      <Typography sx={{ color: '#78716C', fontSize: '0.875rem', mb: 2 }}>
        Une erreur est survenue. Cliquez ci-dessous pour réessayer.
      </Typography>

      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={onRetry}
        sx={{
          borderColor: '#E7E5E4',
          color: '#1C1917',
          fontWeight: 500,
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { borderColor: '#166534' },
        }}
      >
        Réessayer
      </Button>
    </Box>
  );
}
